import React from 'react'
import { useAuth } from '@/hooks/useAuth'
import CircularProgress from '@/components/CircularProgress'
import { useMyClientProgress } from '@/api/trpc'
import { VarianceGauge, ComparativeBarChart } from '@/lib/components/charts'
import {
  Target,
  AlertTriangle,
  Building2,
  Shield,
  BarChart3,
  Award,
  TrendingUp,
  TrendingDown
} from 'lucide-react'
import { usePageTitle } from '@/stores/page-title-store'

/**
 * My Business Targets Page - Director's personal PROA dashboard
 *
 * SECURITY: This page uses tRPC `progress.getMyClientProgress` which:
 * - Returns ONLY percentages and status - never raw financial values
 * - Filters clients based on the authenticated user's assignments
 * - Ensures directors can only see their own clients' progress
 */
export default function MyBusinessTargetsPage() {
  const { user } = useAuth()
  usePageTitle('Minhas Metas')
  const currentYear = new Date().getFullYear()

  // Use progress hook to fetch MY client progress (returns only percentages)
  const {
    data,
    isLoading,
    error,
    refetch
  } = useMyClientProgress(
    { year: currentYear },
    { enabled: !!user?.email }
  )

  // Get client progress data from tRPC response
  const clients = data?.clients || []
  const summary = data?.summary

  // Calculate stats for display
  const clientsAhead = clients.filter(c => c.status === 'ahead').length
  const clientsOnTrack = clients.filter(c => c.status === 'on-track').length
  const clientsBehind = clients.filter(c => c.status === 'behind' || c.status === 'critical').length

  if (!user) {
    return (

        <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6 animate-fade-in-up">
          <Shield className="h-16 w-16 text-brand-red animate-pulse" />
          <p className="text-3xl font-bold text-white font-display">Acesso Negado</p>
          <p className="text-brand-gray-light text-center max-w-md font-body text-lg">
            Você precisa estar logado para visualizar suas metas de negócio.
          </p>
        </div>

    )
  }

  // Loading state
  if (isLoading) {
    return (

        <div className="space-y-8 animate-fade-in-up">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-brand-gray-light font-body">Carregando metas de negócio...</p>
            </div>
          </div>
        </div>

    )
  }

  // Error state
  if (error) {
    return (

        <div className="space-y-8 animate-fade-in-up">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h3 className="font-display text-lg font-medium text-white mb-2">Erro ao carregar dados</h3>
              <p className="text-brand-gray max-w-md mx-auto mb-4">{error.message}</p>
              <button
                onClick={() => refetch()}
                className="btn-primary"
              >
                Tentar Novamente
              </button>
            </div>
          </div>
        </div>

    )
  }

  return (

      <div className="space-y-8 animate-fade-in-up">
        {/* Date Badge */}
        <div className="flex justify-end">
          <div className="text-sm text-brand-gray font-body bg-brand-dark-light/50 px-4 py-2 rounded-monumenta border border-gray-800/50">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-brand-primary rounded-full animate-pulse"></div>
              <span>Metas {currentYear} • Dados até: {data?.asOfDate ? new Date(data.asOfDate).toLocaleDateString('pt-BR') : '-'}</span>
            </div>
          </div>
        </div>

        {clients.length === 0 ? (
          // No clients found
          <div className="card text-center">
            <div className="flex flex-col items-center space-y-6 py-12">
              <Target className="h-16 w-16 text-brand-gray animate-pulse" />
              <div>
                <h3 className="text-2xl font-display font-bold text-white mb-3">
                  Nenhuma Meta Encontrada
                </h3>
                <p className="text-brand-gray-light font-body text-lg max-w-md mx-auto">
                  Não encontramos metas de negócio associadas ao seu usuário.
                  Entre em contato com a administração se isso for um erro.
                </p>
              </div>
              <div className="bg-brand-dark-light rounded-monumenta p-4 border border-brand-gray/20">
                <p className="text-sm text-brand-gray">
                  <strong>Usuário:</strong> {user.name} ({user.email})
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Summary Metrics - All percentages, no raw values */}
            <div className="dashboard-grid lg:grid-cols-4">
              <div className="card-hover">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 rounded-monumenta bg-brand-primary/10">
                    <Target className="w-6 h-6 text-brand-primary" />
                  </div>
                  <span className="text-sm text-brand-gray bg-brand-dark-lighter px-3 py-1 rounded-full">
                    Clientes
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="text-2xl font-display font-bold text-white">
                    {clients.length}
                  </div>
                  <p className="text-sm text-brand-gray">
                    cliente{clients.length !== 1 ? 's' : ''} atribuído{clients.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              <div className="card-hover">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 rounded-monumenta bg-success/10">
                    <TrendingUp className="w-6 h-6 text-success" />
                  </div>
                  <span className="text-sm text-brand-gray bg-brand-dark-lighter px-3 py-1 rounded-full">
                    Acima da Meta
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="text-2xl font-display font-bold text-success">
                    {clientsAhead}
                  </div>
                  <p className="text-sm text-brand-gray">
                    {clients.length > 0 ? Math.round((clientsAhead / clients.length) * 100) : 0}% dos clientes
                  </p>
                </div>
              </div>

              <div className="card-hover">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 rounded-monumenta bg-brand-blue/10">
                    <BarChart3 className="w-6 h-6 text-brand-blue" />
                  </div>
                  <span className="text-sm text-brand-gray bg-brand-dark-lighter px-3 py-1 rounded-full">
                    Atingimento
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="text-2xl font-display font-bold text-white">
                    {summary?.achievementPercent.toFixed(1) || 0}%
                  </div>
                  <p className="text-sm text-brand-gray">
                    média consolidada
                  </p>
                </div>
              </div>

              <div className="card-hover">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-monumenta ${
                    summary?.status === 'ahead' ? 'bg-success/10' :
                    summary?.status === 'on-track' ? 'bg-warning/10' : 'bg-error/10'
                  }`}>
                    <Award className={`w-6 h-6 ${
                      summary?.status === 'ahead' ? 'text-success' :
                      summary?.status === 'on-track' ? 'text-warning' : 'text-error'
                    }`} />
                  </div>
                  <span className="text-sm text-brand-gray bg-brand-dark-lighter px-3 py-1 rounded-full">
                    Variação
                  </span>
                </div>
                <div className="space-y-2">
                  <div className={`text-2xl font-display font-bold ${
                    (summary?.variancePercent || 0) >= 0 ? 'text-success' : 'text-error'
                  }`}>
                    {(summary?.variancePercent || 0) >= 0 ? '+' : ''}{summary?.variancePercent.toFixed(1) || 0}%
                  </div>
                  <p className="text-sm text-brand-gray">
                    vs meta PROA
                  </p>
                </div>
              </div>
            </div>

            {/* Progress Gauge and Status */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card">
                <h3 className="font-display text-xl font-bold text-white mb-4">
                  Atingimento Consolidado
                </h3>
                <div className="flex justify-center">
                  <VarianceGauge
                    actual={summary?.achievementPercent || 0}
                    target={100}
                    label="Meta PROA"
                    size="lg"
                    showPercentage={true}
                  />
                </div>
              </div>

              <div className="card">
                <h3 className="font-display text-xl font-bold text-white mb-4">
                  Distribuição por Status
                </h3>
                <div className="space-y-4 p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-brand-gray flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-success" />
                      Acima da Meta:
                    </span>
                    <span className="text-success font-bold">{clientsAhead} clientes</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-brand-gray flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-warning" />
                      No Alvo (90-100%):
                    </span>
                    <span className="text-warning font-bold">{clientsOnTrack} clientes</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-brand-gray flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-error" />
                      Abaixo da Meta:
                    </span>
                    <span className="text-error font-bold">{clientsBehind} clientes</span>
                  </div>
                  <div className="border-t border-brand-gray/20 pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-brand-gray">Status Geral:</span>
                      <span className={`font-bold uppercase ${
                        summary?.status === 'ahead' ? 'text-success' :
                        summary?.status === 'on-track' ? 'text-warning' :
                        summary?.status === 'behind' ? 'text-yellow-500' : 'text-error'
                      }`}>
                        {summary?.status === 'ahead' ? 'Acima da Meta' :
                         summary?.status === 'on-track' ? 'No Alvo' :
                         summary?.status === 'behind' ? 'Atenção' : 'Crítico'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Client Progress Comparison Chart */}
            <div className="card">
              <h3 className="font-display text-xl font-bold text-white mb-4">
                Progresso por Cliente (% vs Meta)
              </h3>
              <ComparativeBarChart
                data={clients.map(c => ({
                  name: c.clientName,
                  target: 100,
                  actual: c.achievementPercent,
                  variance: c.variancePercent
                }))}
                height={Math.max(400, clients.length * 60)}
              />
            </div>

            {/* Client Results Grid - percentages only */}
            <div className="card">
              <h3 className="font-display text-2xl font-bold text-white mb-8 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
                Progresso por Cliente
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {clients.map((client, index) => {
                  const isHighPerformance = client.achievementPercent >= 90
                  const isAhead = client.status === 'ahead'

                  return (
                    <div key={`${client.clientCode}-${index}`} className={`card-interactive group p-6 transition-all duration-200 ${
                      isAhead ? 'border-success/30 bg-success/5' :
                      isHighPerformance ? 'border-brand-primary/30 bg-brand-primary/5' : ''
                    }`}>
                      {/* Header */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-monumenta bg-brand-primary/10 group-hover:bg-brand-primary/20 transition-colors duration-200">
                            <Building2 className="w-6 h-6 text-brand-primary group-hover:scale-110 transition-transform duration-200" />
                          </div>
                          <div>
                            <h3 className="font-display text-lg font-bold text-white group-hover:text-brand-primary transition-colors duration-200">
                              {client.clientName}
                            </h3>
                            {client.role && (
                              <span className="text-xs text-brand-gray">
                                {client.role === 'PRIMARY_DIRECTOR' ? 'Diretor Principal' : 'Diretor Secundário'}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="group-hover:scale-105 transition-transform duration-200">
                          <CircularProgress percentage={Math.floor(client.achievementPercent)} />
                        </div>
                      </div>

                      <div className="space-y-4 mt-6">
                        {/* Achievement */}
                        <div className="flex justify-between items-center p-3 bg-brand-dark/30 rounded-monumenta">
                          <span className="text-sm text-brand-gray-light font-body flex items-center gap-2">
                            <Target className="w-4 h-4" />
                            Atingimento
                          </span>
                          <span className={`text-sm font-display font-bold ${
                            client.achievementPercent >= 100 ? 'text-success' :
                            client.achievementPercent >= 90 ? 'text-warning' : 'text-error'
                          }`}>
                            {client.achievementPercent.toFixed(1)}%
                          </span>
                        </div>

                        {/* Variance */}
                        <div className="flex justify-between items-center p-3 bg-brand-dark/30 rounded-monumenta">
                          <span className="text-sm text-brand-gray-light font-body flex items-center gap-2">
                            {client.variancePercent >= 0 ? (
                              <TrendingUp className="w-4 h-4 text-success" />
                            ) : (
                              <TrendingDown className="w-4 h-4 text-error" />
                            )}
                            Variação
                          </span>
                          <span className={`text-sm font-display font-bold ${
                            client.variancePercent >= 0 ? 'text-success' : 'text-error'
                          }`}>
                            {client.variancePercent >= 0 ? '+' : ''}{client.variancePercent.toFixed(1)}%
                          </span>
                        </div>

                        {/* Status */}
                        <div className="flex justify-between items-center p-3 bg-brand-dark/30 rounded-monumenta">
                          <span className="text-sm text-brand-gray-light font-body">Status</span>
                          <span className={`text-xs font-bold uppercase px-2 py-1 rounded ${
                            client.status === 'ahead' ? 'bg-success/20 text-success' :
                            client.status === 'on-track' ? 'bg-warning/20 text-warning' :
                            client.status === 'behind' ? 'bg-yellow-500/20 text-yellow-500' :
                            'bg-error/20 text-error'
                          }`}>
                            {client.status === 'ahead' ? 'Acima' :
                             client.status === 'on-track' ? 'No Alvo' :
                             client.status === 'behind' ? 'Atenção' : 'Crítico'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </>
        )}
      </div>

  )
}
